import { v4 as uuid } from 'uuid';

import { parseDate } from '~/eds/src/utils/parseDate';
import { FieldType } from '~/evifields/src/types/FieldType';
import { Filter } from '~/evifields/src/types/Filter';
import { OperatorId } from '~/evifields/src/types/OperatorId';
import {
  DashboardChart,
  Dashboard as DashboardType,
} from '~/redux/api/methods/dashboardV2';
import { Chart } from '~/types/DashboardV2';

import { SearchFilterRecord, SearchQuery } from '../advanced-search/types';
import { buildQuery } from '../advanced-search/utils/build-query';
import { EntityType } from './types';

export const toTableViewFields = (columnOrder: string[]) =>
  columnOrder.map((field_id, column_number) => ({ field_id, column_number }));

export const extractFeaturedClauses = (activeFilters: Filter[]): string[] => {
  type ClauseFilter = Filter & { values: Array<{ provision?: string }> };
  return (
    activeFilters.filter(
      (filter) =>
        filter.fieldId === 'clause' && filter.operatorId === 'contains_any',
    ) as ClauseFilter[]
  ).map((f) => f.values[0]?.provision ?? '');
};

export const buildQueryToSave = ({
  queryBuilder,
  searchFilters,
  activeFilters,
  searchText,
}: {
  queryBuilder?: SearchQuery;
  searchFilters?: SearchFilterRecord;
  activeFilters: Filter[];
  searchText?: string;
}): SearchQuery => {
  if (queryBuilder && searchFilters) return queryBuilder;
  if (activeFilters?.length || searchFilters) {
    return buildQuery({
      booleanQuery: searchText,
      crossFilters: [],
      filters: activeFilters,
      searchFilters,
    });
  }
  return [];
};

const CROSS_FILTER_OPERATORS: Record<string, OperatorId> = {
  age: 'between',
  boolean: 'equals',
  text: 'text_contains_any',
  date: 'date_between',
  enum: 'equals',
  enum_set: 'contains_all',
  number: 'between',
};

const getValuesAndOperator = (
  type: FieldType,
  value: string,
): { values: unknown[]; operatorId: OperatorId } => {
  switch (type) {
    case 'age':
      return {
        values: value.split(' - ').map(Number),
        operatorId: CROSS_FILTER_OPERATORS[type],
      };
    case 'date': {
      const dateValues = value.split(' to ').map((date) => parseDate(date));
      return {
        values: dateValues,
        operatorId:
          dateValues.length > 1 ? CROSS_FILTER_OPERATORS[type] : 'date_on',
      };
    }
    case 'number': {
      const numberValues = `${value}`
        .split(' - ')
        .map((v) => Number(v.replaceAll(',', '')));
      return {
        values: numberValues,
        operatorId:
          numberValues.length > 1 ? CROSS_FILTER_OPERATORS[type] : 'equals',
      };
    }
    case 'boolean':
      return {
        values: [],
        operatorId: value === 'True' ? 'is_true' : 'is_false',
      };
    case 'enum':
    case 'enum_set':
    default:
      return {
        values: [value],
        operatorId: CROSS_FILTER_OPERATORS[type],
      };
  }
};

export const toFilter = (
  fieldId: string,
  value: string,
  type: FieldType,
): Filter => ({
  id: uuid(),
  fieldId,
  ...getValuesAndOperator(type, value),
});

export const toDashboardData = ({
  dashboard,
  charts,
  columnOrder,
  selectedFilters,
  intervals,
  chartWidths,
  query,
  entity,
}: {
  dashboard: DashboardType;
  charts: Chart[];
  columnOrder: string[];
  selectedFilters: Filter[];
  intervals: Record<string, string>;
  chartWidths: Record<string, string>;
  query: SearchQuery;
  entity: EntityType;
}): DashboardType => ({
  charts: charts.map(
    (item, index): DashboardChart => ({
      filter_id: item.id,
      sorted_index: index,
      interval: intervals[item.id],
      width: chartWidths[item.id] || undefined,
      type: item.type,
    }),
  ),
  columns: columnOrder,
  filters: selectedFilters
    .filter((f) => !f.values.length && !f.operatorId)
    .map((f) => f.fieldId.toString()),
  id: dashboard.id,
  name: dashboard.name,
  entity,
  query,
  is_default: !!dashboard.is_default,
  creatorId: dashboard.creatorId,
});
